<template>
    <div class="ui-page">
        <ui-page-header :title="page.title" :back="dataset.generalMeetingId ? `/agcs/${dataset.generalMeetingId}` : '/agcs'"
            back-text="Detalhes da Assembleia">
            <template #subtitle>
                <span v-if="dataset.generalMeetingName">Assembleia: {{ meetingTitle }}</span>
                <span v-if="dataset.generalMeetingName && dataset.votingName"> · </span>
                <span v-if="dataset.votingName">Votação: {{ dataset.votingName }}</span>
            </template>
            <adhesion-terms :voting-id="dataset.generalMeetingVotingId" :client-id="dataset.clientId" :status="dataset.votingStatus" :subject="dataset.votingSubject" @saved="filterSearch" />
            <v-btn color="primary" outlined
                :disabled="!dataset.generalMeetingId"
                :loading="ballot.loading" @click="openBallotExport">
                <v-icon left small>mdi-file-excel-outline</v-icon>
                Exportar cédula Excel
            </v-btn>
        </ui-page-header>

        <!-- Estatísticas -->
        <v-row>
            <v-col cols="6" sm="3">
                <v-card flat class="stat-card">
                    <v-card-text class="text-center pa-4">
                        <div class="stat-value" style="color: #16A34A;">{{ yesCount }}</div>
                        <div class="stat-label">SIM</div>
                        <div class="stat-pct">{{ yesPercentage }}%</div>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="6" sm="3">
                <v-card flat class="stat-card">
                    <v-card-text class="text-center pa-4">
                        <div class="stat-value" style="color: #DC2626;">{{ noCount }}</div>
                        <div class="stat-label">NÃO</div>
                        <div class="stat-pct">{{ noPercentage }}%</div>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="6" sm="3">
                <v-card flat class="stat-card">
                    <v-card-text class="text-center pa-4">
                        <div class="stat-value" style="color: #6B7280;">{{ nullCount }}</div>
                        <div class="stat-label">ABSTENÇÃO</div>
                        <div class="stat-pct">fora da apuração</div>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="6" sm="3">
                <v-card flat class="stat-card">
                    <v-card-text class="text-center pa-4">
                        <div class="stat-value" style="color: #D97706;">{{ pendingCount }}</div>
                        <div class="stat-label">SEM VOTO</div>
                        <div class="stat-pct">fora da apuração</div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Graficos do resultado: rodinha por cabeca e por credito, e panorama por classe -->
        <ui-section title="Gráficos do resultado" class="mt-4">
            <template #actions>
                <v-btn small outlined color="primary" @click="downloadResultCharts(false)">
                    <v-icon left small>mdi-download</v-icon> Baixar PNG
                </v-btn>
                <v-btn small depressed color="primary" @click="projectionDialog = true">
                    <v-icon left small>mdi-projector-screen-outline</v-icon> Projetar
                </v-btn>
            </template>
            <v-row>
                <v-col cols="12" md="4">
                    <div class="ui-label font-weight-medium mb-1">Votos por cabeça</div>
                    <highcharts ref="chartHeads" :options="headsPieOptions"></highcharts>
                </v-col>
                <v-col cols="12" md="4">
                    <div class="ui-label font-weight-medium mb-1">Votos por crédito (R$)</div>
                    <highcharts ref="chartAmount" :options="amountPieOptions"></highcharts>
                </v-col>
                <v-col cols="12" md="4">
                    <div class="ui-label font-weight-medium mb-1">{{ panoramaTitle }}</div>
                    <highcharts v-if="panoramaRows.length" ref="chartClasses" :options="classesPanoramaOptions"></highcharts>
                    <div v-else class="panorama-empty">Ainda não há dados para exibir o panorama.</div>
                </v-col>
            </v-row>
        </ui-section>

        <!-- Modo projecao: so os graficos, em tela cheia, sem o restante do backoffice -->
        <v-dialog v-model="projectionDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
            <v-card class="projection-card">
                <v-toolbar flat dense color="white">
                    <v-toolbar-title class="projection-title">
                        {{ meetingTitle }} · {{ dataset.votingName }}
                    </v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn text color="primary" @click="downloadResultCharts(true)"><v-icon left small>mdi-download</v-icon> Baixar PNG</v-btn>
                    <v-btn icon @click="projectionDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-toolbar>
                <v-card-text class="projection-body">
                    <div class="projection-stats">
                        <div><span class="projection-stat-value" style="color:#16A34A">{{ yesCount }}</span><span class="projection-stat-label">SIM · {{ yesPercentage }}%</span></div>
                        <div><span class="projection-stat-value" style="color:#DC2626">{{ noCount }}</span><span class="projection-stat-label">NÃO · {{ noPercentage }}%</span></div>
                        <div><span class="projection-stat-value" style="color:#6B7280">{{ nullCount }}</span><span class="projection-stat-label">ABSTENÇÃO</span></div>
                        <div><span class="projection-stat-value" style="color:#D97706">{{ pendingCount }}</span><span class="projection-stat-label">SEM VOTO</span></div>
                        <div v-if="dataset.classResult"><span class="projection-stat-value" :style="{ color: overallTie ? '#D97706' : (overallApproved ? '#16A34A' : '#DC2626') }">{{ overallResultLabel }}<small v-if="overallTie" class="ml-2">Empate</small></span><span class="projection-stat-label">RESULTADO</span></div>
                    </div>
                    <v-row>
                        <v-col cols="12" md="4"><div class="projection-chart-title">Votos por cabeça</div><highcharts ref="projHeads" :options="bigChart(headsPieOptions)"></highcharts></v-col>
                        <v-col cols="12" md="4"><div class="projection-chart-title">Votos por crédito (R$)</div><highcharts ref="projAmount" :options="bigChart(amountPieOptions)"></highcharts></v-col>
                        <v-col cols="12" md="4"><div class="projection-chart-title">{{ panoramaTitle }}</div><highcharts v-if="panoramaRows.length" ref="projClasses" :options="bigChart(classesPanoramaOptions)"></highcharts><div v-else class="panorama-empty">Ainda não há dados para exibir o panorama.</div></v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-dialog>

        <ui-section title="Resultado por classe" flush>
            <template #actions>
                <!-- Previa pelos votos enquanto o cenario nao foi apurado -->
                <ui-badge v-if="dataset.classResult && dataset.classResult.source === 'live'" tone="warning"
                    title="Calculado pelos votos registrados. O resultado oficial sai na apuração do cenário.">
                    Prévia · aguardando apuração ({{ overallResultLabel }}<small v-if="overallTie" class="ml-2">Empate</small>)
                </ui-badge>
                <ui-badge v-else-if="dataset.classResult" :tone="overallTie ? 'warning' : (overallApproved ? 'success' : 'danger')">
                    {{ overallResultLabel }}<small v-if="overallTie" class="ml-2">Empate</small>
                </ui-badge>
                <ui-badge v-else tone="neutral" text="Aguardando apuração" />
            </template>
            <div v-if="classResultCards.length" class="class-result-grid pa-4">
                <v-card
                    v-for="item in classResultCards"
                    :key="item.class_id || 'final'"
                    flat
                    :class="['class-result-card', classResultTone(item)]">
                    <div class="d-flex justify-space-between align-start">
                        <div>
                            <div class="class-result-title">{{ item.class_name }}</div>
                            <div class="class-result-rule">{{ item.requires_amount ? 'Cabeça + crédito' : 'Cabeça' }}</div>
                        </div>
                        <span :class="['class-result-badge', classResultTone(item)]">
                            {{ getClassResultText(item) }}<small v-if="item.tie" class="d-block">Empate</small>
                        </span>
                    </div>
                    <div class="class-result-metrics">
                        <div>
                            <span class="metric-label">Cabeça</span>
                            <strong>{{ formatPercent(item.head_percentage) }}</strong>
                            <small>{{ item.yes_heads }}/{{ item.effective_heads }}</small>
                        </div>
                        <div>
                            <span class="metric-label">Crédito</span>
                            <strong>{{ item.requires_amount ? formatPercent(item.amount_percentage) : 'N/A' }}</strong>
                            <small>{{ item.requires_amount ? formatMoney(item.yes_amount) : 'Não aplicado' }}</small>
                        </div>
                    </div>
                </v-card>
            </div>
            <v-data-table
                :headers="classResultHeaders"
                :items="classResultCards"
                hide-default-footer
                disable-sort
                :no-data-text="classResultEmptyText">
                <template v-slot:[`item.class_name`]="{ item }">
                    <div class="ui-cell-main">{{ item.class_name }}</div>
                    <div class="ui-cell-sub">{{ item.rule }}</div>
                </template>

                <template v-slot:[`item.heads`]="{ item }">
                    <span>{{ item.yes_heads }} de {{ item.effective_heads }}</span>
                    <span class="ui-muted">({{ formatPercent(item.head_percentage) }})</span>
                    <div class="ui-cell-sub">
                        {{ item.abstention_heads }} abstenção · {{ item.pending_heads }} sem voto
                    </div>
                </template>

                <template v-slot:[`item.amount`]="{ item }">
                    <template v-if="item.requires_amount">
                        <span>{{ formatMoney(item.yes_amount) }} de {{ formatMoney(item.effective_amount) }}</span>
                        <span class="ui-muted">({{ formatPercent(item.amount_percentage) }})</span>
                        <div class="ui-cell-sub">
                            Base: créditos votantes
                        </div>
                    </template>
                    <span v-else class="ui-muted">Não aplicado</span>
                </template>

                <template v-slot:[`item.status`]="{ item }">
                    <ui-badge :tone="classResultBadgeTone(item)">
                        {{ getClassResultText(item) }}<small v-if="item.tie" class="ml-1">Empate</small>
                    </ui-badge>
                </template>
            </v-data-table>
        </ui-section>

        <!-- Credores + Ações -->
        <ui-section :title="`Credores (${dataList.count || 0})`" flush class="voting-creditors">
            <template #actions>
                <v-btn
                    v-if="(canCloseVoting) && $allowed('gm.voting')"
                    small
                    color="error"
                    depressed
                    :loading="dataset.statusLoading"
                    @click="closeVoting()">
                    <v-icon left small>mdi-stop-circle-outline</v-icon>
                    Encerrar votação
                </v-btn>
                <v-btn v-if="$allowed('create')" small outlined color="primary" @click="openManualCreditorDialog()">
                    <v-icon left small>mdi-account-plus-outline</v-icon>
                    Incluir credor
                </v-btn>
                <v-btn small color="primary" depressed @click="openManualVoteDialog()">
                    <v-icon left small>mdi-vote-outline</v-icon>
                    Voto manual
                </v-btn>
                <ui-action-menu small label="Relatórios" icon="mdi-file-chart-outline" :items="[
                    { text: 'Excel da votação', icon: 'mdi-microsoft-excel', handler: generateCSV },
                    { text: 'Laudo da assembleia (PDF)', icon: 'mdi-file-pdf-box', handler: generatePDF },
                    { text: 'Ressalvas de voto (anexo da ata)', icon: 'mdi-file-document-outline', handler: generateReservationsPDF },
                    { text: 'Resultado da assembleia', icon: 'mdi-account-details', disabled: !canGenerateAssemblyResult, description: canGenerateAssemblyResult ? null : 'Disponível após encerrar a votação', handler: openAssemblyResulModal },
                    { text: 'Relatório final', icon: 'mdi-chart-bar', handler: generatePdfFinal },
                ]" />
            </template>

            <ui-table-card clearable :can-clear="hasFilters" @clear="clearFilters">
                <template #toolbar>
                    <v-text-field id="name" v-model="dataset.filter.name" placeholder="Buscar pelo nome" prepend-inner-icon="mdi-magnify"
                        dense outlined hide-details class="ui-toolbar__search" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                    <v-text-field v-model="dataset.filter.registration" placeholder="CPF/CNPJ" dense outlined hide-details clearable
                        class="ui-toolbar__field" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                    <v-select :items="classFilterItems" v-model="dataset.filter.type" item-value="id" item-text="name"
                        placeholder="Todas as classes" dense outlined hide-details class="ui-toolbar__field"
                        :menu-props="{ offsetY: true }" @change="applyFilters">
                    </v-select>
                    <v-text-field v-model="dataset.filter.procurator" placeholder="Procurador" dense outlined hide-details clearable
                        class="ui-toolbar__field" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                    <v-select :items="dataset.votingTypes" v-model="dataset.filter.voting" item-value="id" item-text="name"
                        placeholder="Todos os votos" dense outlined hide-details class="ui-toolbar__field"
                        :menu-props="{ offsetY: true }" @change="applyFilters">
                    </v-select>
                </template>

                <v-data-table :headers="dataList.headers" :items="dataList.data" :server-items-length="dataList.count"
                    :options.sync="dataList.options" :loading="dataList.loading" hide-default-footer
                    @update:options="onTableOptions"
                    no-data-text="Nenhuma lista de nomes foi encontrada" loading-text="Carregando dados...">
                    <template v-slot:[`item.name`]="{ item }">
                        <span class="ui-cell-main">{{ item.name }}</span>
                    </template>

                    <template v-slot:[`item.uc_name`]="{ item }">
                        <div class="ui-cell-main">{{ item.uc_name }}</div>
                        <div v-if="item.uc_registration" class="ui-cell-sub">{{ formatRegistrationValue(item.uc_registration) }}</div>
                    </template>

                    <template v-slot:[`item.uc_type`]="{ item }">
                        {{ formatCreditorClasses(item) }}
                    </template>

                    <template v-slot:[`item.procurator_name`]="{ item }">
                        <span :class="{ 'ui-muted': !item.procurator_name }">{{ item.procurator_name || 'N/A' }}</span>
                    </template>

                    <template v-slot:[`item.vote`]="{ item }">
                        <ui-badge :tone="voteBadgeTone(item.vote)">
                            <template v-if="item.vote">
                                {{ item.vote | enum(eventStatus, 'id', 'name') }}
                            </template>
                            <template v-else>SEM VOTO</template>
                        </ui-badge>
                        <!-- Ressalva/justificativa enviada com o voto -->
                        <v-btn v-if="item.reservation" icon x-small color="primary" class="ml-1" title="Ver ressalva do voto" @click="openReservationDialog(item)">
                            <v-icon small>mdi-text-box-outline</v-icon>
                        </v-btn>
                    </template>

                    <template v-slot:[`item.actions`]="{ item }">
                        <!-- Voto em um clique: usado durante a sessao ao vivo -->
                        <div class="d-flex justify-end" style="gap: 6px;">
                            <v-btn small outlined @click.stop="actionGeneralMeeting(item, 1)">Sim</v-btn>
                            <v-btn small outlined @click.stop="actionGeneralMeeting(item, 2)">Não</v-btn>
                            <v-btn small outlined @click.stop="actionGeneralMeeting(item, 3)">Abstenção</v-btn>
                        </div>
                    </template>
                </v-data-table>

                <template #footer>
                    <ui-pagination :total="dataList.count" :page="dataList.options.page" :items-per-page="dataList.options.itemsPerPage"
                        :disabled="dataList.loading" @update:page="pageChanged" @update:itemsPerPage="setPageSize" />
                </template>
            </ui-table-card>
        </ui-section>

        <v-dialog v-model="dialogAssemblyResult" max-width="1080" scrollable :persistent="isLoadingPdfModal">
            <v-card class="voting-result-modal">
                <header class="voting-result-header">
                    <div><span class="voting-result-eyebrow">APURAÇÃO DA VOTAÇÃO</span><h2>Resultado da votação</h2><p>{{ dataset.votingName || 'Votação' }}</p></div>
                    <v-btn icon aria-label="Fechar resultado" :disabled="isLoadingPdfModal" @click="dialogAssemblyResult = false"><v-icon>mdi-close</v-icon></v-btn>
                </header>
                <v-card-text class="voting-result-body" ref="pdfContent">
                    <v-skeleton-loader v-if="assemblyResultData.loading" type="article, table" />
                    <v-alert v-else-if="assemblyResultError" type="error" text>
                        Não foi possível carregar o resultado.
                        <v-btn text small @click="loadResultGeneralMeeting">Tentar novamente</v-btn>
                    </v-alert>
                    <template v-else-if="assemblyFinalResult">
                        <section class="voting-result-verdict" :class="'verdict-' + assemblyResultTone">
                            <div class="verdict-icon"><v-icon :color="assemblyResultTone">{{ assemblyFinalResult.plan ? 'mdi-check-circle-outline' : assemblyFinalResult.tie ? 'mdi-equal-box' : 'mdi-close-circle-outline' }}</v-icon></div>
                            <div><span class="voting-result-eyebrow">RESULTADO FINAL</span><h3>{{ assemblyFinalResult.plan ? 'Aprovado' : assemblyFinalResult.tie ? 'Empate' : 'Rejeitado' }}</h3><p>{{ assemblyResultMessage }}</p></div>
                        </section>
                        <div class="voting-result-metrics">
                            <section v-for="metric in assemblyResultMetrics" :key="metric.key" class="voting-result-metric">
                                <h3>{{ metric.label }}</h3>
                                <div class="voting-result-values">
                                    <div class="result-yes"><span>Sim</span><strong>{{ metric.value.yes }}{{ metric.suffix }}</strong></div>
                                    <div class="result-no"><span>Não</span><strong>{{ metric.value.no }}{{ metric.suffix }}</strong></div>
                                </div>
                            </section>
                        </div>
                        <p class="voting-result-note"><v-icon small>mdi-information-outline</v-icon> Os percentuais deste resumo consideram os votos Sim e Não. Abstenções e votos não registrados aparecem separadamente no panorama da votação.</p>
                        <section v-if="assemblyClassResults.length" class="voting-result-classes">
                            <h3>Resultado por classe</h3>
                            <div class="voting-result-table-wrap">
                                <table class="voting-result-table">
                                    <thead><tr><th>Classe</th><th>Credores · Sim / Não</th><th>Por cabeça · Sim / Não</th><th>Por crédito · Sim / Não</th><th>Resultado</th></tr></thead>
                                    <tbody><tr v-for="(item, index) in assemblyClassResults" :key="index">
                                        <th scope="row">{{ typeof item.class === 'number' ? $options.filters.enum(item.class, dataset.creditorTypes, 'id', 'name') : item.class }}</th>
                                        <td><span class="result-yes">{{ item.creditors_number.yes }}</span> / <span class="result-no">{{ item.creditors_number.no }}</span></td>
                                        <td><span class="result-yes">{{ item.head.yes }}%</span> / <span class="result-no">{{ item.head.no }}%</span></td>
                                        <td><span class="result-yes">{{ item.credit.yes }}%</span> / <span class="result-no">{{ item.credit.no }}%</span></td>
                                        <td><v-chip small label :color="item.tie ? 'warning' : item.plan ? 'success' : 'error'" outlined>{{ item.plan ? 'Aprovado' : 'Rejeitado' }}<span v-if="item.tie"> · Empate</span></v-chip></td>
                                    </tr></tbody>
                                </table>
                            </div>
                        </section>
                    </template>
                    <v-alert v-else text type="info">Resultado ainda não disponível.</v-alert>
                </v-card-text>
                <footer class="voting-result-footer">
                    <span>Exporte o resultado para compartilhar ou arquivar.</span>
                    <div><v-btn text :disabled="isLoadingPdfModal" @click="dialogAssemblyResult = false">Fechar</v-btn><v-btn color="primary" elevation="0" :disabled="assemblyResultData.loading || assemblyResultError || !assemblyFinalResult" :loading="isLoadingPdfModal" @click="generatePdfAssemblyResult"><v-icon left small>mdi-file-pdf-box</v-icon>Baixar resultado em PDF</v-btn></div>
                </footer>
            </v-card>
        </v-dialog>

        <v-dialog v-model="manualDialog.open" max-width="760px" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <h3 class="ui-section-title">{{ manualDialog.mode === 'vote' ? 'Voto manual' : 'Incluir credor na AGC' }}</h3>
                </div>
                <div class="ui-card__body">
                    <v-alert type="info" text dense>Selecione um credor do caso para registrar sua participação. Para créditos exclusivos da AGC, utilize um cenário.</v-alert>

                    <v-row>
                        <v-col cols="12">
                            <v-autocomplete
                                v-model="manualDialog.form.creditor"
                                :items="manualDialog.creditors"
                                :item-text="formatCreditorOption"
                                return-object
                                dense
                                outlined
                                clearable
                                hide-details="auto"
                                label="Credor"
                                :loading="manualDialog.creditorsLoading"
                                @update:search-input="loadAvailableCreditors"
                            />
                        </v-col>
                    </v-row>

                    <v-row v-if="manualDialog.mode === 'vote'">
                        <v-col cols="12" md="4">
                            <v-select
                                v-model="manualDialog.form.vote"
                                :items="eventStatus.filter(item => item.id !== 0)"
                                item-value="id"
                                item-text="name"
                                dense
                                outlined
                                hide-details="auto"
                                label="Voto"
                            />
                        </v-col>
                    </v-row>
                </div>
                <div class="ui-card__footer">
                    <v-btn text @click="closeManualDialog" :disabled="manualDialog.loading">Cancelar</v-btn>
                    <v-btn v-if="$allowed('gm.voting')" color="primary" depressed @click="saveManualDialog" :loading="manualDialog.loading">
                        Salvar
                    </v-btn>
                </div>
            </v-card>
        </v-dialog>
        <!-- Ressalva/justificativa enviada pelo credor com o voto -->
        <v-dialog v-model="reservationDialog.open" max-width="600">
            <v-card v-if="reservationDialog.item" class="ui-card">
                <v-card-title>
                    Ressalva do voto
                    <v-spacer></v-spacer>
                    <v-btn icon aria-label="Fechar" @click="reservationDialog.open = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <div class="mb-1"><strong>{{ reservationDialog.item.uc_name }}</strong></div>
                    <div class="ui-muted mb-4">
                        {{ formatCreditorClasses(reservationDialog.item) }} · Voto:
                        {{ reservationDialog.item.vote ? $options.filters.enum(reservationDialog.item.vote, eventStatus, 'id', 'name') : 'SEM VOTO' }}
                    </div>
                    <div class="ui-label font-weight-medium mb-1">Texto</div>
                    <div class="mb-4" style="white-space: pre-line;">{{ reservationDialog.item.reservation.text || 'Sem texto' }}</div>
                    <div class="ui-label font-weight-medium mb-1">Documento</div>
                    <v-btn v-if="reservationDialog.item.reservation.doc_url" small outlined color="primary" :href="reservationDialog.item.reservation.doc_url" target="_blank">
                        <v-icon left small>mdi-paperclip</v-icon>{{ reservationDialog.item.reservation.doc_name || 'Abrir documento' }}
                    </v-btn>
                    <span v-else class="ui-muted">Nenhum documento anexado.</span>
                </v-card-text>
            </v-card>
        </v-dialog>
        <v-dialog v-model="ballot.open" max-width="560" :persistent="ballot.exporting">
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <h3 class="ui-section-title">Exportar cédula Excel</h3>
                </div>
                <div class="ui-card__body">
                    <p>Votação: <strong>{{ dataset.votingName }}</strong></p>
                    <p>A cédula usará os créditos do cenário selecionado e os votos desta votação.</p>
                    <v-select v-if="ballot.scenarios.length" v-model="ballot.scenarioId"
                        :items="ballot.scenarios" item-text="name" item-value="id"
                        label="Cenário" outlined dense :disabled="ballot.exporting" hide-details />
                    <v-alert v-else type="info" text>
                        Esta assembleia ainda não possui cenário. Crie um em Cenários para exportar a cédula.
                    </v-alert>
                </div>
                <div class="ui-card__footer">
                    <v-btn text :disabled="ballot.exporting" @click="ballot.open = false">Cancelar</v-btn>
                    <v-btn v-if="!ballot.scenarios.length" color="primary" outlined
                        @click="$router.push({ path: `/agcs/${dataset.generalMeetingId}/cenarios`, query: { voting_id: dataset.generalMeetingVotingId } })">
                        Ir para cenários
                    </v-btn>
                    <v-btn v-else color="primary" depressed :disabled="!ballot.scenarioId"
                        :loading="ballot.exporting" @click="exportBallot">Baixar Excel</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { downloadBoardPng } from "@/common/util/chart-export.util";
import { downloadXlsx } from "@/common/util/export-xlsx.util";

import votingTypes from '@/common/data/voting-types.data'
import creditorTypes from '@/common/data/creditor-types.data'
import creditorClassUtil from '@/common/util/creditor-class.util'
import currencyTypes from '@/common/data/currency-types.data'
import string from '@/common/util/string'

import object from '@/common/util/object'
import axios from 'axios'
import _ from 'lodash'
import AdhesionTerms from '@/components/general-meeting/AdhesionTerms.vue'
import { cpf, cnpj } from 'cpf-cnpj-validator'

export default {
    components: { AdhesionTerms },
    name: 'GeneralMeetingVoting',

    data: () => ({
        projectionDialog: false,
        reservationDialog: { open: false, item: null },
        ballot: { open: false, loading: false, exporting: false, scenarios: [], scenarioId: null },
        page: {
            isNew: false,
            title: ''
        },
        menu: false,
        dialogAssemblyResult: false,
        isLoadingPdfModal: false,
        assemblyResultError: false,
        isLoadingModal: false,
        dataset: {
            breadcrumbItems: [],
            date: null,
            generalMeetingSessionLabel: '',
            data: {},
            clients: [],
            filter: {
                name: '',
                registration: '',
                type: '',
                procurator: '',
                voting: '',
                general_meeting_id: -1
            },
            votingTypes: [ {id: "", name: "Todos"}, ...votingTypes],
            creditorTypes: creditorTypes,
            currencyTypes: currencyTypes,
            generalMeetingId: null,
            votingStatus: null,
            classResult: null,
            statusLoading: false
        },
        dataForm: {
            validForm: true,
            addLoading: false,
            updateLoading: false,
            removeLoading: false
        },

        eventStatus: [
            { id: 0, name: 'Pendente' },
            { id: 1, name: 'SIM' },
            { id: 2, name: 'NÃO' },
            { id: 3, name: 'ABSTENÇÃO' }
        ],
        assemblyResultData: {
            itemKey: 'name',
            headers: [
                { text: 'CLASSE', value: 'class', class: 'header-label' },
                { text: 'N. CREDORES', value: 'creditors_number', class: 'header-label' },
                { text: '% CABEÇA', value: 'head', class: 'header-label' },
                { text: '% CRÉDITO', value: 'credit', class: 'header-label' },
                { text: 'RESULTADO', value: 'plan', class: 'header-label' }
            ],
            items: [],
            loading: false
        },
        dataList: {
            headers: [
                { text: 'Nome', value: 'uc_name', sortable: true },
                { text: 'Classe', value: 'uc_type', sortable: true },
                { text: 'Procurador', value: 'procurator_name', sortable: true },
                { text: 'Votação', value: 'vote', sortable: true },
                { text: '', value: 'actions', sortable: false, align: 'end', width: 240 }
            ],
            data: [],
            loading: false,
            //count: 11,
            options: {
                page: 1,
                itemsPerPage: 10,
                sortBy: ['uc_name'],
                sortDesc: [false]
            },
            // Ordenacao em uso, para recarregar so quando ela muda.
            sortKey: 'uc_name|false',
            footerOptions: {
                itemsPerPageText: 'Itens por página:',
                itemsPerPageOptions: [10, 20, 30, 40, 50],
                pageText: '{0}-{1} de {2}'
            }
        },
        classResultHeaders: [
            { text: 'Classe', value: 'class_name' },
            { text: 'Cabeça', value: 'heads' },
            { text: 'Crédito', value: 'amount' },
            { text: 'Resultado', value: 'status', sortable: false }
        ],
        yesCount: 0,
        noCount: 0,
        nullCount: 0,
        pendingCount: 0,

        yesPercentage: 0,
        noPercentage: 0,
        nullPercentage: 0,
        pendingPercentage: 0
        ,
        votingRefreshInterval: null,
        votingRefreshLoading: false,
        manualDialog: {
            open: false,
            mode: 'creditor',
            loading: false,
            creditorsLoading: false,
            creditors: [],
            lastCreditorSearch: null,
            creditorRequestKey: null,
            creditorRequestPromise: null,
            form: {
                source: 'existing',
                creditor: null,
                name: '',
                registration: '',
                type: 1,
                currency: 1,
                amount: 0,
                credit_items: [{ type: 1, currency: 1, amount: 0 }],
                vote: ''
            }
        }
    }),

    // mounted() {
    //     let data = this.$route.params.data;

    //     this.dataset.generalMeetingId = data.general_meeting_id;
    //     this.dataset.generalMeetingName = data.general_meeting_name;
    //     this.dataset.voting_id = data.voting_id;
    //     this.dataset.votingName = data.voting_name;
    //     this.dataset.voting_status = data.voting_status;
    //     this.dataset.voting_type = data.voting_type;


    //     console.log("Recebido: ", this.dataset.generalMeetingId, this.dataset.general_meeting_name, this.dataset.voting_id, this.dataset.voting_name,
    //         this.dataset.voting_status, this.dataset.voting_type
    //     )

    //     //this.loadVotingResult();

    //     this.loadData()

    // },

    created() {
        this.dataset.generalMeetingVotingId = this.$route.params.id
        this.loadData()
    },

    beforeDestroy() {
        this.stopVotingRefresh()
    },

    computed: {
        // Nome da AGC com a convocacao do tipo registrado ao lado (o nome e texto livre).
        meetingTitle() {
            const name = this.dataset.generalMeetingName || '';
            const label = this.dataset.generalMeetingSessionLabel || '';
            return label ? `${name} · ${label}` : name;
        },
        classFilterItems() {
            return [{ id: '', name: 'Todas as classes' }, ...(this.dataset.creditorTypes || []).filter((item) => Number(item.id) !== 5)]
        },

        hasFilters() {
            const filter = this.dataset.filter
            return Boolean(filter.name || filter.registration || filter.procurator) || filter.type !== '' || filter.voting !== '';
        },

        computedDateFormatted() {
            return this.formatDate(this.dataset.data.date)
        },

        checkedCountComputed() {
            return this.dataList.data.filter(item => item.checked).length
        },
        uncheckedCountComputed() {
            return this.dataList.data.filter(item => !item.checked).length
        },
        checkedPercentageComputed() {
            return ((this.checkedCountComputed / this.dataList.data.length) * 100).toFixed(2)
        },
        uncheckedPercentageComputed() {
            return ((this.uncheckedCountComputed / this.dataList.data.length) * 100).toFixed(2)
        },
        votingSubject() {
            return this.dataset.votingSubject || 'plan'
        },
        // Plano: aprovado quando todas as classes com quorum aprovam (art. 45).
        // Suspensao e outras deliberacoes: maioria do credito presente (art. 42).
        // Cartoes do resultado: por classe (quorum qualificado) ou o resultado
        // final unico (quorum simples, que nao separa por classe).
        classResultCards() {
            const result = this.dataset.classResult
            if (!result) return []
            if (Array.isArray(result.classes) && result.classes.length) return result.classes
            if (result.summary) return [{ ...result.summary, approved: result.approved, class_id: 'final', class_name: result.summary.class_name || 'Resultado final' }]
            return []
        },

        classResultEmptyText() {
            if (!this.dataset.classResult) return 'Aguardando apuração do cenário'
            return 'Nenhum voto registrado ainda'
        },

        overallApproved() {
            return !!(this.dataset.classResult && this.dataset.classResult.approved)
        },
        overallTie() {
            const result = this.dataset.classResult
            if (!result) return false
            return Number(result.voting_type) === 1
                ? !!(result.summary && result.summary.head_tie)
                : (result.classes || []).some(item => item.tie)
        },
        overallResultLabel() {
            return this.overallApproved ? 'Aprovado' : 'Rejeitado'
        },
        // As roscas usam a base da apuração (SIM + NÃO), igual aos cards e ao resultado:
        // abstenção e sem voto ficam só na contagem dos cards.
        headsPieOptions() {
            return this.pieOptions([
                { name: 'SIM', y: Number(this.yesCount || 0), color: '#16A34A' },
                { name: 'NÃO', y: Number(this.noCount || 0), color: '#DC2626' },
            ], 'credor(es)')
        },
        // Rodinha por credito: valores somados das classes.
        amountPieOptions() {
            const result = this.dataset.classResult || {}
            const summary = result.summary || {}
            // O resultado apurado no cenario traz os valores so por classe: soma as classes
            // quando o resumo nao tem valor.
            const classes = Array.isArray(result.classes) ? result.classes : []
            const amountOf = (key) => (summary[key] !== undefined
                ? Number(summary[key] || 0)
                : classes.reduce((sum, item) => sum + Number(item[key] || 0), 0))
            return this.pieOptions([
                { name: 'SIM', y: amountOf('yes_amount'), color: '#16A34A' },
                { name: 'NÃO', y: amountOf('no_amount'), color: '#DC2626' },
            ], 'R$', true)
        },
        panoramaRows() {
            const result = this.dataset.classResult
            if (!result) return []
            if (Array.isArray(result.classes) && result.classes.length) return result.classes
            return result.summary ? [{ ...result.summary, class_name: 'Resultado geral' }] : []
        },
        panoramaTitle() {
            const result = this.dataset.classResult
            return result && Array.isArray(result.classes) && result.classes.length
                ? 'Panorama por classe (cabeças)' : 'Panorama geral (cabeças)'
        },
        // Usa a mesma base consolidada retornada pela API nas votacoes simples.
        classesPanoramaOptions() {
            const classes = this.panoramaRows
            const categories = classes.map((item) => String(item.class_name || '').replace('Classe ', '').split(' - ')[0])
            const serie = (name, field, color) => ({ name, color, data: classes.map((item) => Number(item[field] || 0)) })
            return {
                title: null,
                credits: { enabled: false },
                chart: { type: 'column', backgroundColor: 'transparent', height: 260 },
                xAxis: { categories, lineColor: '#E5E7EB', tickColor: '#E5E7EB' },
                yAxis: { title: { text: null }, allowDecimals: false, gridLineColor: '#F3F4F6', stackLabels: { enabled: true } },
                legend: { enabled: true },
                plotOptions: { column: { stacking: 'normal', borderWidth: 0 } },
                tooltip: { shared: true },
                series: [
                    serie('SIM', 'yes_heads', '#16A34A'),
                    serie('NÃO', 'no_heads', '#DC2626'),
                    serie('ABSTENÇÃO', 'abstention_heads', '#6B7280'),
                    serie('SEM VOTO', 'pending_heads', '#D97706'),
                ],
            }
        },
        // Objeto da votacao para as frases: plano, suspensao ou a propria deliberacao.
        votingObject() {
            const name = this.dataset.votingName ? String(this.dataset.votingName).trim() : ''
            switch (this.votingSubject) {
                case 'suspension':
                    return { text: 'a suspensão da assembleia', feminine: true, article: 42 }
                case 'other':
                    return { text: name ? `a deliberação "${name}"` : 'a deliberação', feminine: true, article: 42 }
                default:
                    return { text: 'o plano de recuperação', feminine: false, article: 45 }
            }
        },
        assemblyFinalResult() {
            return this.assemblyResultData.items.find(item => item.final) || null
        },
        assemblyClassResults() {
            return this.assemblyResultData.items.filter(item => !item.final)
        },
        assemblyResultTone() {
            const result = this.assemblyFinalResult
            return result && result.tie ? 'warning' : result && result.plan ? 'success' : 'error'
        },
        assemblyResultMetrics() {
            const result = this.assemblyFinalResult
            if (!result) return []
            return [
                { key: 'creditors_number', label: 'Credores votantes', suffix: '', value: result.creditors_number },
                { key: 'head', label: 'Votos por cabeça', suffix: '%', value: result.head },
                { key: 'credit', label: 'Votos por crédito', suffix: '%', value: result.credit }
            ]
        },
        assemblyResultStatusText() {
            const finalResult = this.assemblyResultData.items.find(item => item.final)
            const approved = !!(finalResult && finalResult.plan)
            if (!approved && finalResult && finalResult.tie) return 'objeto de empate, sem maioria'
            return this.votingObject.feminine
                ? (approved ? 'aprovada' : 'rejeitada')
                : (approved ? 'aprovado' : 'rejeitado')
        },
        assemblyResultMessage() {
            const object = this.votingObject
            return `Conforme o critério de apuração configurado, ${object.text} foi ${this.overallApproved ? (object.feminine ? "aprovada" : "aprovado") : (object.feminine ? "rejeitada" : "rejeitado")}.${this.overallTie ? " Empate sinalizado para conferência." : ""}`
        },
        canCloseVoting() {
            return [2, 3].includes(Number(this.dataset.votingStatus))
        },
        canGenerateAssemblyResult() {
            return [4, 5].includes(Number(this.dataset.votingStatus))
        }
    },

    methods: {
        async openBallotExport() {
            this.ballot.loading = true;
            this.ballot.scenarioId = null;
            this.ballot.scenarios = [];
            try {
                const [scenarios, votings] = await Promise.all([
                    axios.get(`/api/admin/general-meeting-votings/scenarios/${this.dataset.generalMeetingId}`),
                    axios.get(`/api/admin/general-meeting-votings/action/${this.dataset.generalMeetingId}`)
                ]);
                this.ballot.scenarios = scenarios.data.content || [];
                const voting = (votings.data.content || []).find(item => Number(item.id) === Number(this.dataset.generalMeetingVotingId));
                const linked = voting && this.ballot.scenarios.find(item => Number(item.id) === Number(voting.scenario_id));
                this.ballot.scenarioId = linked ? linked.id : (this.ballot.scenarios.length === 1 ? this.ballot.scenarios[0].id : null);
                this.ballot.open = true;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.ballot.loading = false;
            }
        },

        async exportBallot() {
            if (!this.ballot.scenarioId || this.ballot.exporting) return;
            this.ballot.exporting = true;
            try {
                const response = await axios.get(
                    `/api/admin/general-meeting-votings/scenario/${this.ballot.scenarioId}/ballot/export`,
                    { responseType: 'blob', params: { general_meeting_voting_id: this.dataset.generalMeetingVotingId } }
                );
                const disposition = response.headers['content-disposition'] || '';
                const match = disposition.match(/filename="?([^";]+)"?/i);
                const url = window.URL.createObjectURL(response.data);
                const link = document.createElement('a');
                link.href = url;
                link.download = match ? match[1] : `cedula-votacao-${this.dataset.generalMeetingVotingId}-cenario-${this.ballot.scenarioId}.xlsx`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                this.ballot.open = false;
            } catch (error) {
                if (error.response && error.response.data instanceof Blob) {
                    try { error.response.data = JSON.parse(await error.response.data.text()); } catch (_) { /* Keep original response. */ }
                }
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.ballot.exporting = false;
            }
        },

        applyFilters() {
            this.dataList.options.page = 1
            this.filterSearch()
        },

        filterDebounced: _.debounce(function () {
            this.applyFilters()
        }, 400),

        clearFilters() {
            this.dataset.filter = { name: '', registration: '', type: '', procurator: '', voting: '', general_meeting_id: -1 }
            this.applyFilters()
        },

        // Clique no cabecalho: ordena pela coluna e volta para a primeira pagina.
        onTableOptions(options) {
            const key = `${(options.sortBy && options.sortBy[0]) || ''}|${!!(options.sortDesc && options.sortDesc[0])}`
            if (key === this.dataList.sortKey) return
            this.dataList.sortKey = key
            this.dataList.options.page = 1
            this.filterSearch()
        },

        sortParams() {
            const sortBy = this.dataList.options.sortBy && this.dataList.options.sortBy[0]
            if (!sortBy) return {}
            return { sort_by: sortBy, sort_desc: this.dataList.options.sortDesc && this.dataList.options.sortDesc[0] ? 1 : 0 }
        },

        // A troca de tamanho e seguida de update:page(1), que recarrega.
        setPageSize(size) {
            this.dataList.options.itemsPerPage = size
        },

        pageChanged(page) {
            this.dataList.options.page = page
            this.filterSearch()
        },

        pageSizeChanged(size) {
            this.dataList.options.itemsPerPage = size
            this.filterSearch(true)
        },

        goBack() {
            this.$router.go(-1)
        },

        async loadData() {
            this.setPage()
            this.listBreadCrumbItems()

            // await this.loadClients()
            //await this.load()
            await this.filterSearch()
            await this.updateSummary()
        },

        setPage() {
            this.page.title = `Votação`
        },

        formatDate(date) {
            if (!date) return null

            return this.$moment(date).format('DD/MM/YYYY')
        },
        parseDate(date) {
            if (!date) return null

            const [month, day, year] = date.split('/')
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
        },

        listBreadCrumbItems() {
            this.dataset.breadcrumbItems = [
                {
                    text: 'Homepage',
                    disabled: false,
                    to: '/home',
                    exact: true
                }
            ]

            this.dataset.breadcrumbItems.push({
                text: 'AGC',
                disabled: false,
                to: '/agcs',
                exact: true
            })

            this.dataset.breadcrumbItems.push({
                text: `Votação`,
                disabled: true,
                exact: true
            })
        },

        async updateSummary() {
            if (this.dataset.stats) {
                const stats = this.dataset.stats
                const total = Number(stats.total || 0)
                const effectiveTotal = Number(stats.yes || 0) + Number(stats.no || 0)

                this.yesCount = Number(stats.yes || 0)
                this.noCount = Number(stats.no || 0)
                this.nullCount = Number(stats.abstention || 0)
                this.pendingCount = Number(stats.pending || 0)

                this.yesPercentage = effectiveTotal ? ((this.yesCount / effectiveTotal) * 100).toFixed(2) : 0
                this.noPercentage = effectiveTotal ? ((this.noCount / effectiveTotal) * 100).toFixed(2) : 0
                this.nullPercentage = total ? ((this.nullCount / total) * 100).toFixed(2) : 0
                this.pendingPercentage = total ? ((this.pendingCount / total) * 100).toFixed(2) : 0
                return
            }

            const yesItems = this.dataList.data.filter( item => item.vote === 1 )
            const noItems = this.dataList.data.filter( item => item.vote === 2 )
            const nullItems = this.dataList.data.filter( item => item.vote === 3 )
            const pendingItems = this.dataList.data.filter( item => !item.vote || item.vote === 0 )
            
            this.yesCount = yesItems.length
            this.noCount = noItems.length
            this.pendingCount = pendingItems.length
            this.nullCount = nullItems.length

            const effectiveItems = yesItems.length + noItems.length

            this.yesPercentage = effectiveItems
                ? ((this.yesCount / effectiveItems) * 100).toFixed(2)
                : 0
            this.noPercentage = effectiveItems
                ? ((this.noCount / effectiveItems) * 100).toFixed(2)
                : 0
            this.pendingPercentage = this.pendingCount
                ? ((this.pendingCount / this.dataList.data.length) * 100).toFixed(2)
                : 0
            this.nullPercentage = this.nullCount
                ? ((this.nullCount / this.dataList.data.length) * 100).toFixed(2)
                : 0
        },

        // O tema global pinta de cinza (com !important) todo chip sem classe de cor do
        // Vuetify; por isso a cor do voto vai como estilo inline.
        chipStyle(background, text = '#FFFFFF') {
            return {
                'background-color': background + ' !important',
                'border-color': background + ' !important',
                color: text + ' !important',
            };
        },

        voteChipStyle(vote) {
            return this.chipStyle(this.getVoteBg(vote), this.getVoteText(vote));
        },

        voteBadgeTone(vote) {
            switch (Number(vote)) {
                case 1: return 'success'
                case 2: return 'danger'
                case 3: return 'neutral'
                default: return 'warning'
            }
        },

        classResultBadgeTone(item) {
            return {
                'tone-approved': 'success',
                'tone-rejected': 'danger',
                'tone-tie': 'warning',
                'tone-none': 'neutral',
            }[this.classResultTone(item)]
        },

        getVoteBg(vote) {
            switch (Number(vote)) {
                case 1: return '#16A34A'
                case 2: return '#DC2626'
                case 3: return '#6B7280'
                default: return '#D97706'
            }
        },

        getVoteText(vote) {
            switch (Number(vote)) {
                case 1:
                case 2:
                case 3:
                    return '#FFFFFF'
                default: return '#FFFFFF'
            }
        },

        resultChipText(approved) {
            return approved ? 'Aprovado' : 'Rejeitado'
        },

        formatCreditorClasses(item) {
            const classIds = item && item.uc_classes
                ? String(item.uc_classes).split(',').filter(Boolean)
                : [item && item.uc_type].filter(Boolean)

            return classIds
                .map(id => this.$options.filters.enum(Number(id), this.dataset.creditorTypes, 'id', 'name'))
                .join(', ')
        },

        formatPercent(value) {
            return `${Number(value || 0).toFixed(2)}%`
        },

        formatMoney(value) {
            return Number(value || 0).toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL'
            })
        },

        getClassResultColor(item) {
            if (!item || !(item.has_quorum === undefined ? Number(item.effective_heads) > 0 : item.has_quorum)) return '#6B7280'
            if (item.tie) return '#D97706'
            return item.approved ? '#16A34A' : '#DC2626'
        },

        // Tom visual do cartao: aprovado, rejeitado ou sem quorum.
        pieOptions(points, unit, money = false) {
            const total = points.reduce((sum, point) => sum + point.y, 0)
            const fmt = (value) => (money ? Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : `${value} ${unit}`)
            return {
                title: null,
                credits: { enabled: false },
                chart: { type: 'pie', backgroundColor: 'transparent', height: 260 },
                tooltip: { pointFormatter() { return `<b>${this.name}</b>: ${fmt(this.y)} (${total ? ((this.y / total) * 100).toFixed(1) : 0}%)` } },
                plotOptions: {
                    pie: {
                        innerSize: '55%',
                        borderWidth: 0,
                        dataLabels: { enabled: true, format: '{point.name}<br>{point.percentage:.1f}%', style: { fontSize: '11px', fontWeight: '600' } },
                        showInLegend: false,
                    },
                },
                series: [{ name: 'Votos', data: total ? points : [{ name: 'Sem dados', y: 1, color: '#E5E7EB' }] }],
            }
        },

        // Graficos maiores no modo projecao.
        bigChart(options) {
            return { ...options, chart: { ...options.chart, height: 420, backgroundColor: '#FFFFFF' } };
        },

        // Um PNG unico, no arranjo da projecao: titulo, numeros e os tres graficos com subtitulo.
        async downloadResultCharts(fromProjection = false) {
            // So true conta como projecao (o @click sem argumento passaria o evento do clique).
            const refs = fromProjection === true ? ['projHeads', 'projAmount', 'projClasses'] : ['chartHeads', 'chartAmount', 'chartClasses'];
            const chartOf = (ref) => (this.$refs[ref] && this.$refs[ref].chart) || null;
            if (!chartOf(refs[0]) && !chartOf(refs[1])) {
                this.$dialog.notify.error('Os gráficos ainda não foram carregados. Tente novamente.', { position: 'top-right', timeout: 3000 });
                return;
            }
            const stats = [
                { value: this.yesCount, label: `SIM · ${this.yesPercentage}%`, color: '#16A34A' },
                { value: this.noCount, label: `NÃO · ${this.noPercentage}%`, color: '#DC2626' },
                { value: this.nullCount, label: 'ABSTENÇÃO', color: '#6B7280' },
                { value: this.pendingCount, label: 'SEM VOTO', color: '#D97706' },
            ];
            if (this.dataset.classResult) {
                stats.push({
                    value: this.overallTie ? `${this.overallResultLabel} (Empate)` : this.overallResultLabel,
                    label: 'RESULTADO',
                    color: this.overallTie ? '#D97706' : (this.overallApproved ? '#16A34A' : '#DC2626'),
                });
            }
            const base = String(this.dataset.votingName || 'votacao').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
            const ok = await downloadBoardPng({
                title: [this.meetingTitle, this.dataset.votingName].filter(Boolean).join(' · '),
                stats,
                panels: [
                    { title: 'Votos por cabeça', chart: chartOf(refs[0]) },
                    { title: 'Votos por crédito (R$)', chart: chartOf(refs[1]) },
                    { title: this.panoramaTitle, chart: chartOf(refs[2]), emptyText: 'Ainda não há dados para exibir o panorama.' },
                ],
                fileName: `${base}-resultado`,
            });
            if (!ok) this.$dialog.notify.error('Não foi possível gerar o PNG.', { position: 'top-right', timeout: 3000 });
        },

        classResultTone(item) {
            if (!item || !(item.has_quorum === undefined ? Number(item.effective_heads) > 0 : item.has_quorum)) return 'tone-none'
            if (item.tie) return 'tone-tie'
            return item.approved ? 'tone-approved' : 'tone-rejected'
        },

        // Metade exata por cabeca aprova, mas fica sinalizada; metade exata por credito e empate.
        getClassResultText(item) {
            if (!item || !(item.has_quorum === undefined ? Number(item.effective_heads) > 0 : item.has_quorum)) return 'Sem quórum'
            return item.approved ? 'Aprovado' : 'Rejeitado'
        },

        getColorByStatus(status) {
            switch (status) {
                case 0:
                    return 'warning'
                case 1:
                    return 'accent'
                case 2:
                    return 'error'
                default:
                    break
            }
        },

        getFormattedDate() {
            const currentDate = new Date();
            const day = String(currentDate.getDate()).padStart(2, '0'); 
            const month = String(currentDate.getMonth() + 1).padStart(2, '0');
            const year = currentDate.getFullYear(); 

            return `${day}-${month}-${year}`;
        },

        // async load() {
        //     const id =  this.dataset.generalMeetingId

        //     try {
        //         const result = await axios.get(`/api/admin/general-meetings/${id}`)

        //         const data = result.data.content

        //         this.dataset.data = data
        //     } catch (error) {
        //         this.$dialog.notify.error(this.$codes(error), {
        //             position: 'top-right',
        //             timeout: 2000
        //         })
        //     }
        // },

        startVotingRefresh() {
            if (this.votingRefreshInterval) return

            this.votingRefreshInterval = window.setInterval(async () => {
                if (Number(this.dataset.votingStatus) !== 2) {
                    this.stopVotingRefresh()
                    return
                }

                if (this.votingRefreshLoading) return

                this.votingRefreshLoading = true
                try {
                    await this.filterSearch({ showLoading: false })
                } finally {
                    this.votingRefreshLoading = false
                }
            }, 5000)
        },

        stopVotingRefresh() {
            if (!this.votingRefreshInterval) return

            window.clearInterval(this.votingRefreshInterval)
            this.votingRefreshInterval = null
        },

        syncVotingRefresh() {
            if (Number(this.dataset.votingStatus) === 2) {
                this.startVotingRefresh()
                return
            }

            this.stopVotingRefresh()
        },

        async filterSearch(options = {}) {
            const showLoading = typeof options === 'boolean'
                ? options
                : options.showLoading !== false

            let search = Object.fromEntries(Object.entries(this.dataset.filter).filter(([_, v]) => v))

            search = !object.isEmpty(search) ? search : {}

            //search.general_meeting_id = this.dataset.generalMeetingId
            search.general_meeting_voting_id = this.dataset.generalMeetingVotingId
            search.presence = '1'

            if (!search.voting && this.dataset.filter.voting === 0) search.voting = 0

            if (showLoading) this.dataList.loading = true

            var limit = this.dataList.options.itemsPerPage
            var page = this.dataList.options.page

            try {

                const result = await axios.get('/api/admin/general-meeting-votings/voting-list', {
                    params: { page: page, limit: limit, search: search, ...this.sortParams() }
                })

                const resultContent = result.data.content

                this.dataList.data = resultContent.items

                this.dataset.generalMeetingId = resultContent.general_meeting_id
                this.dataset.clientId = resultContent.client_id

                // Rotulo de classe nos votos e select de lancamento manual.
                // Inclui ocultas: votos ja registrados nao podem perder o nome.
                this.dataset.creditorTypes = await creditorClassUtil.listLabels(resultContent.client_id)
                this.dataset.generalMeetingName = resultContent.general_meeting_name
                this.dataset.generalMeetingSessionLabel = resultContent.general_meeting_session_label || ''
                this.dataset.votingName = resultContent.voting_name
                this.dataset.votingStatus = resultContent.voting_status
                this.$set(this.dataset, 'votingSubject', resultContent.voting_subject || 'plan')
                this.dataset.stats = resultContent.stats || null
                this.dataset.classResult = resultContent.class_result || null

                this.dataList.count = resultContent.paging ? resultContent.paging.total : resultContent.items.length
                this.updateSummary()
                this.syncVotingRefresh()
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 })
            } finally {
                if (showLoading) this.dataList.loading = false
            }
        },

        openManualCreditorDialog() {
            this.openManualDialog('creditor')
        },

        openManualVoteDialog() {
            this.openManualDialog('vote')
        },

        async openManualDialog(mode) {
            this.manualDialog.mode = mode
            this.manualDialog.form = {
                source: 'existing',
                creditor: null,
                name: '',
                registration: '',
                type: 1,
                currency: 1,
                amount: 0,
                credit_items: [{ type: 1, currency: 1, amount: 0 }],
                vote: ''
            }
            this.manualDialog.lastCreditorSearch = null
            this.manualDialog.creditorRequestKey = null
            this.manualDialog.creditorRequestPromise = null
            this.manualDialog.open = true
            await this.loadAvailableCreditors()
        },

        closeManualDialog() {
            this.manualDialog.open = false
        },

        addManualCreditItem() {
            this.manualDialog.form.credit_items.push({ type: 1, currency: 1, amount: 0 })
        },

        removeManualCreditItem(index) {
            if (this.manualDialog.form.credit_items.length === 1) return
            this.manualDialog.form.credit_items.splice(index, 1)
        },

        formatCreditorOption(item) {
            if (!item) return ''
            const registration = item.registration ? ` - ${this.formatRegistrationValue(item.registration)}` : ''
            // Cada linha do QGC (classe) e uma opcao propria: o voto manual e por classe.
            const className = item.type
                ? this.$options.filters.enum(Number(item.type), this.dataset.creditorTypes, 'id', 'name')
                : null
            const classLabel = className ? ` · ${className}` : ''
            const amountLabel = item.amount !== undefined && item.amount !== null
                ? ` · ${this.formatMoney(item.amount)}`
                : ''
            return `${item.name || ''}${registration}${classLabel}${amountLabel}`
        },

        formatRegistrationValue(value) {
            const rawValue = String(value || '').replace(/\D/g, '')
            if (!rawValue) return ''
            return rawValue.length === 14 ? cnpj.format(rawValue) : cpf.format(rawValue)
        },

        formatManualRegistration() {
            const rawValue = String(this.manualDialog.form.registration || '').replace(/\D/g, '')
            if (!rawValue) {
                this.manualDialog.form.registration = ''
                return
            }
            this.manualDialog.form.registration = rawValue.length > 11 ? cnpj.format(rawValue.slice(0, 14)) : cpf.format(rawValue.slice(0, 11))
        },

        async loadAvailableCreditors(searchText = '') {
            if (!this.dataset.clientId) return

            const normalizedSearch = String(searchText || '').trim()
            const requestKey = `${this.dataset.clientId}:${normalizedSearch}`

            if (
                this.manualDialog.creditorRequestKey === requestKey &&
                this.manualDialog.creditorsLoading &&
                this.manualDialog.creditorRequestPromise
            ) {
                return this.manualDialog.creditorRequestPromise
            }

            if (
                this.manualDialog.creditorRequestKey === requestKey &&
                this.manualDialog.creditors.length > 0
            ) {
                return
            }

            this.manualDialog.lastCreditorSearch = normalizedSearch
            this.manualDialog.creditorRequestKey = requestKey
            this.manualDialog.creditorsLoading = true

            const requestPromise = (async () => {
                const search = { client_id: this.dataset.clientId }
                if (normalizedSearch) search.name = normalizedSearch

                const result = await axios.get('/api/admin/users-creditors', {
                    params: { page: 1, limit: 50, search }
                })

                this.manualDialog.creditors = result.data.content.data
            })()

            this.manualDialog.creditorRequestPromise = requestPromise

            try {
                await requestPromise
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 })
            } finally {
                if (this.manualDialog.creditorRequestKey === requestKey) {
                    this.manualDialog.creditorsLoading = false
                    this.manualDialog.creditorRequestPromise = null
                }
            }
        },

        buildManualPayload() {
            const payload = {
                with_presence: true,
                participation_type: 1
            }

            if (!this.manualDialog.form.creditor) throw new Error('Selecione um credor.')
            payload.user_creditor_id = this.manualDialog.form.creditor.id

            if (this.manualDialog.mode === 'vote') {
                if (!this.manualDialog.form.vote) throw new Error('Selecione o voto.')
                payload.vote = this.manualDialog.form.vote
            }

            return payload
        },

        async saveManualDialog() {
            this.manualDialog.loading = true

            try {
                const payload = this.buildManualPayload()

                if (this.manualDialog.mode === 'vote') {
                    await axios.post(`/api/admin/general-meeting-votings/${this.dataset.generalMeetingVotingId}/manual-vote`, payload)
                    this.$dialog.notify.info('Voto manual contabilizado com sucesso!', { position: 'top-right', timeout: 2000 })
                } else {
                    await axios.post(`/api/admin/general-meetings/${this.dataset.generalMeetingId}/creditors`, payload)
                    this.$dialog.notify.info('Credor incluído na AGC com sucesso!', { position: 'top-right', timeout: 2000 })
                }

                this.closeManualDialog()
                await this.filterSearch()
                await this.updateSummary()
            } catch (error) {
                this.$dialog.notify.error(error.message || this.$codes(error), { position: 'top-right', timeout: 5000 })
            } finally {
                this.manualDialog.loading = false
            }
        },

        // async loadClients() {
        //     try {
        //         const result = await axios.get(`/api/clients`)

        //         const data = result.data.content

        //         this.dataset.clients = data
        //     } catch (error) {
        //         this.$dialog.notify.error(this.$codes(error), {
        //             position: 'top-right',
        //             timeout: 2000
        //         })
        //     }
        // },

        async loadResultGeneralMeeting() {
            try {
                this.assemblyResultData.loading = true;
                this.assemblyResultError = false;
                this.assemblyResultData.items = [];
                if (!this.dataset.classResult) {
                    await this.filterSearch({ showLoading: false })
                }

                const classResult = this.dataset.classResult || { classes: [], approved: false }
                const resultData = []

                // Uma linha por classe com credor votante. As porcentagens de credito
                // saem sempre, mesmo nas classes em que a lei conta so por cabeca.
                const percent = (part, whole) => (Number(whole || 0) > 0 ? ((Number(part || 0) / Number(whole)) * 100).toFixed(0) : '0')
                const toRow = (item) => ({
                    class: item.class_id || item.class_name,
                    creditors_number: { yes: Number(item.yes_heads || 0), no: Number(item.no_heads || 0) },
                    head: {
                        yes: percent(item.yes_heads, item.effective_heads),
                        no: percent(item.no_heads, item.effective_heads)
                    },
                    credit: {
                        yes: percent(item.yes_amount, item.effective_amount),
                        no: percent(item.no_amount, item.effective_amount)
                    },
                    plan: !!item.approved,
                    tie: !item.approved && !!item.tie,
                    head_tie: !!item.head_tie && !item.requires_amount
                })

                const classItems = (classResult.classes || []).filter((item) => Number(item.total_heads || 0) > 0)

                for (let item of classItems) {
                    resultData.push(toRow(item))
                }

                // Linha final: totais de todas as classes (vindos da API), e nao so da pagina exibida.
                const summary = classResult.summary || null
                const finalRow = summary
                    ? toRow({ ...summary, approved: classResult.approved, tie: this.overallTie, requires_amount: Number(classResult.voting_type) !== 1 })
                    : {
                        class: null,
                        creditors_number: { yes: this.yesCount, no: this.noCount },
                        head: {
                            yes: Number(this.yesPercentage || 0).toFixed(0),
                            no: Number(this.noPercentage || 0).toFixed(0)
                        },
                        credit: { yes: '0', no: '0' },
                        plan: false
                    }

                resultData.push({
                    ...finalRow,
                    class: 'RESULTADO FINAL',
                    plan: this.overallApproved,
                    final: true
                })

                this.assemblyResultData.items = resultData

            } catch (error) {
                this.assemblyResultError = true;
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            } finally {
                this.assemblyResultData.loading = false;
            }
        },

        buildPayload() {
            const data = {
                name: this.dataset.data.name,
                date: this.dataset.data.date,
                client: this.dataset.data.client_id,
                description: this.dataset.data.description
            }

            return data
        },

        async generateCSV() {
            const proceedAction = await this.$dialog.confirm({
                text: 'Deseja exportar os dados para Excel?',
                title: 'Exportar dados',
                actions: {
                    false: 'Cancelar',
                    true: 'Exportar'
                }
            })

            if (!proceedAction) return

            // Exporta todas as linhas (uma por classe do credor), e nao so a pagina exibida.
            let data = this.dataList.data

            try {
                let search = Object.fromEntries(Object.entries(this.dataset.filter).filter(([_, v]) => v))
                search = !object.isEmpty(search) ? search : {}
                search.general_meeting_voting_id = this.dataset.generalMeetingVotingId
                search.presence = '1'
                if (!search.voting && this.dataset.filter.voting === 0) search.voting = 0

                const result = await axios.get('/api/admin/general-meeting-votings/voting-list', {
                    params: { page: 1, limit: 100000, search, ...this.sortParams() }
                })

                data = (result.data.content && result.data.content.items) || data
            } catch (error) {
                // Sem resposta da API, exporta o que ja esta carregado na tela.
            }

            if (!data || data.length == 0) {
                this.$dialog.notify.error('Sem dados para exportar!', {
                    position: 'top-right',
                    timeout: 2000
                })

                return
            }

            const newData = data.map(objeto => ({
                'NOME CREDOR': (objeto.uc_name || '').toUpperCase(),
                'EMAIL CREDOR': objeto.uc_email,
                'CLASSE': this.formatCreditorClasses(objeto) || 'N/A',
                'CREDITO': this.formatMoney(objeto.amount),
                'NOME PROCURADOR': objeto.procurator_name || 'N/A',
                // 'SOLICITADO EM': objeto.created_at,
                VOTACAO: objeto.vote
                    ? objeto.vote === 1
                        ? 'SIM'
                        : objeto.vote === 2
                            ? 'NAO'
                            : objeto.vote === 3
                                ? 'ABSTENÇÃO'
                                : 'SEM VOTO'
                    : 'SEM VOTO'
            }))

            try {
                await downloadXlsx(newData, this.dataset.votingName + '_' + this.getFormattedDate(), { sheetName: 'Votação' })

                this.$dialog.notify.info('Planilha Excel gerada com sucesso!', {
                    position: 'top-right',
                    timeout: 2000
                })
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            }
        },

        openReservationDialog(item) {
            this.reservationDialog = { open: true, item };
        },

        // Anexo da ata: PDF so com as ressalvas de voto.
        async generateReservationsPDF() {
            this.dataset.loading = true
            try {
                const response = await axios.post(
                    `/api/admin/general-meetings/voting-reservations-pdf/${this.dataset.generalMeetingVotingId}`,
                    {},
                    { responseType: 'blob' }
                )
                const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', 'ressalvas-de-voto-' + this.dataset.votingName + '_' + this.getFormattedDate() + '.pdf')
                document.body.appendChild(link)
                link.click()
                link.remove()
                window.URL.revokeObjectURL(url)
            } catch (error) {
                this.$dialog.notify.error('Não foi possível gerar o PDF das ressalvas.', { position: 'top-right', timeout: 3000 })
            } finally {
                this.dataset.loading = false
            }
        },

        async generatePDF() {
            //if (!item || !item.id) return;

            const proceedAction = await this.$dialog.confirm({
                text: 'Gerar PDF da votação ?',
                title: 'Gerar PDF',
                actions: {
                    false: 'Cancelar',
                    true: 'Gerar PDF'
                }
            })

            if (!proceedAction) return

            console.log('gerando pdf da votacao')

            console.log(this.dataset.data.id)

            this.dataset.loading = true

            try {
                const response = await axios.post(
                    `/api/admin/general-meetings/voting-pdf/${this.dataset.generalMeetingVotingId}`,
                    {},
                    { responseType: 'blob' }
                )

                const blob = new Blob([response.data], { type: 'application/pdf' })
                const url = window.URL.createObjectURL(blob)

                const formattedDate = this.getFormattedDate();

                // Create a link element to trigger the download
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', 'laudo-votacao-' + this.dataset.votingName + '_' + formattedDate + '.pdf')

                // Append the link to the body and click it to start the download
                document.body.appendChild(link)
                link.click()

                // Clean up by revoking the URL object
                window.URL.revokeObjectURL(url)
            } catch (error) {
                console.error('Failed to generate PDF:', error)

                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            } finally {
                this.dataset.loading = false
            }
        },

        async actionGeneralMeeting(item, status) {


            console.log("Item-send: ", item.uc_id, status)

            const index = this.dataList.data.findIndex(x => x.uc_id === item.uc_id)

            const voting = this.dataList.data[index].vote

            // !voting
            //     ? (this.dataList.data[index].voting = { status })
            //     : (this.dataList.data[index].voting.status = status)

            try {
                const response = await axios.post(`/api/admin/general-meetings/voting`, {
                    user_creditor_id: this.dataList.data[index].uc_id,
                    general_meeting_id: this.dataset.generalMeetingId,
                    general_meeting_voting_id: this.dataset.generalMeetingVotingId,
                    vote: status
                })

                this.$dialog.notify.info('Voto contabilizado com sucesso', {
                    position: 'top-right',
                    timeout: 2000
                })
            } catch (error) {
                voting ? (this.dataList.data[index].voting = voting) : null

                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            }

            await this.filterSearch();

            await this.updateSummary();
        },

        async closeVoting() {
            const proceed = await this.$dialog.confirm({
                title: 'Encerrar votação',
                text: 'Deseja encerrar esta votação? Após o encerramento, os votos não poderão mais ser alterados.',
                actions: {
                    false: 'Cancelar',
                    true: 'Encerrar'
                }
            })

            if (!proceed) return

            this.dataset.statusLoading = true

            try {
                await axios.patch(`/api/admin/general-meeting-votings/set-status/${this.dataset.generalMeetingVotingId}`, {
                    status: 4
                })

                this.$dialog.notify.info('Votação encerrada com sucesso.', {
                    position: 'top-right',
                    timeout: 3000
                })

                await this.filterSearch()
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 5000
                })
            } finally {
                this.dataset.statusLoading = false
            }
        },

        async openAssemblyResulModal() {
            if (!this.canGenerateAssemblyResult) {
                this.$dialog.notify.error('O resultado só pode ser gerado após encerrar a votação.', {
                    position: 'top-right',
                    timeout: 5000
                })
                return
            }

            // this.isLoadingModal = true;
            this.dialogAssemblyResult = true
            await this.loadResultGeneralMeeting()
            // await this.loadResultData();
            //  this.isLoadingModal = false;
        },

        // async loadResultData() {

        //     try {

        //         const result = await axios.get(`/api/admin/general-meetings/${id}`);

        //         this.dataList.assemblyResultData = result.data.content;

        //     } catch (error) {
        //         this.$dialog.notify.error(this.$codes(error), {
        //             position: 'top-right',
        //             timeout: 2000
        //         })
        //     }
        // },

        // PDF gerado na API, no mesmo tema dos demais documentos (antes era impressao do modal).
        async generatePdfAssemblyResult() {
            this.isLoadingPdfModal = true
            try {
                const response = await axios.post('/api/admin/general-meetings/result-pdf/' + this.dataset.generalMeetingVotingId, {}, { responseType: 'blob' })
                const blob = new Blob([response.data], { type: 'application/pdf' })
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', 'resultado-assembleia-' + this.dataset.votingName + '_' + this.getFormattedDate() + '.pdf')
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                window.URL.revokeObjectURL(url)
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 })
            } finally {
                this.isLoadingPdfModal = false
            }
        },

        async generatePdfFinal() {

            const proceedAction = await this.$dialog.confirm({
                text: 'Gerar PDF da votação <strong>' + this.dataset.votingName + '</strong> ?',
                title: 'Gerar relatório final',
                actions: {
                    false: 'Cancelar',
                    true: 'Gerar PDF'
                }
            })

            if (!proceedAction) return;

            console.log("gerando pdf da assembleia")

            this.dataset.loading = true;


            try {
                const response = await axios.post(`/api/admin/general-meetings/generate-pdf/${this.dataset.generalMeetingVotingId}`, {}, { responseType: 'blob' })

                const formattedDate = this.getFormattedDate();

                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);

                // Create a link element to trigger the download
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'relatorio-final-' + this.dataset.votingName + '_' + formattedDate + '.pdf');

                // Append the link to the body and click it to start the download
                document.body.appendChild(link);
                link.click();

                // Clean up by revoking the URL object
                window.URL.revokeObjectURL(url);

            } catch (error) {
                console.error('Failed to generate PDF:', error)

                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            } finally {
                this.dataset.loading = false;
            }


            // try {

            //     const result = await axios.delete(`/api/admin/general-meetings/${item.id}`)

            //     this.$dialog.notify.info("Assembleia excluída com sucesso", { position: "top-right", timeout: 2000 });

            // } catch (error) {

            //     this.$dialog.notify.error(this.$codes(error), {
            //         position: 'top-right',
            //         timeout: 2000
            //     })

            // } finally {
            //     this.dataset.loading = false;
            // }

        },
    }
}
</script>

<style lang="scss">
// Tabela de credores dentro da ui-section: sem borda dupla.
.voting-creditors .ui-table-card {
    border: 0;
    border-radius: 0;
}

.access-container {
    border: 1px solid var(--v-gray-base) !important;
}

.custom-chip {
    background: var(--v-white-base) !important;
}

.title-page {
    color: var(--v-primary-base);
    // font-family: Montserrat;
    font-size: 40px;
    font-style: normal;
    font-weight: 700;
    line-height: 38px;
}

.section-info-title {
    color: var(--v-primary-base);
    // font-family: Montserrat;
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
}

.section-info-value {
    // font-family: Montserrat;
    font-size: 14px;
    font-style: normal;
}

.list-card {
    max-height: calc(100vh - 400px);
    overflow-y: auto;
}

.stat-card {
    background-color: #FFFFFF !important;
}

.stat-value {
    font-size: 28px;
    font-weight: 700;
    line-height: 1.2;
}

.stat-label {
    font-size: 11px;
    font-weight: 700;
    color: #8E8EA1;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-top: 4px;
}

.stat-pct {
    font-size: 13px;
    font-weight: 500;
    color: #8E8EA1;
    margin-top: 2px;
}

.class-result-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 16px;
    padding-top: 4px;
}

// Cartao por classe: borda colorida pelo resultado, fundo levemente tingido e
// sombra, para se destacar do fundo da tela.
.class-result-card {
    border: 1px solid #E5E7EB;
    border-left-width: 6px;
    border-radius: 10px;
    padding: 16px 18px;
    background: #FFFFFF !important;
    box-shadow: 0 2px 8px rgba(37, 37, 56, 0.08);
    transition: box-shadow 0.2s;

    &:hover {
        box-shadow: 0 6px 16px rgba(37, 37, 56, 0.14);
    }

    &.tone-approved {
        border-left-color: #16A34A;
        background: linear-gradient(180deg, #F0FDF4 0%, #FFFFFF 60%) !important;
    }

    &.tone-rejected {
        border-left-color: #DC2626;
        background: linear-gradient(180deg, #FEF2F2 0%, #FFFFFF 60%) !important;
    }

    &.tone-none {
        border-left-color: #9CA3AF;
        background: linear-gradient(180deg, #F9FAFB 0%, #FFFFFF 60%) !important;
    }

    &.tone-tie {
        border-left-color: #D97706;
        background: linear-gradient(180deg, #FFFBEB 0%, #FFFFFF 60%) !important;
    }
}

.class-result-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: #FFFFFF;
    white-space: nowrap;

    &.tone-approved { background: #16A34A; }
    &.tone-rejected { background: #DC2626; }
    &.tone-none { background: #6B7280; }
    &.tone-tie { background: #D97706; }
}

.class-result-title {
    color: #252538;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.25;
}

.class-result-rule {
    color: #8E8EA1;
    font-size: 12px;
    margin-top: 2px;
}

.class-result-metrics {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin-top: 14px;

    > div {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
    }

    strong {
        color: #252538;
        font-size: 22px;
        line-height: 1.1;
    }

    small {
        color: #8E8EA1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.metric-label {
    color: #8E8EA1;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
}

.summary-item {
    margin-bottom: 8px;
    border: 1px solid #ccc;
    padding: 8px;
    border-radius: 4px;
}

.summary-value {
    font-weight: bold;
}

.result-pair {
    display: flex;
    justify-content: center;
    gap: 20px;
}

.result-cell {
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    white-space: nowrap;
}

.label-row {
    font-size: 11px;
    color: #6B7280;
    letter-spacing: 0.04em;
    line-height: 1.2;
}

.value-row {
    font-weight: 700;
    font-size: 14px !important;
    line-height: 1.3;
}

.dasboard-voting-table tbody tr td {
    border-right: 1px solid #ccc;
}

.modal-title {
    color: var(--v-primary-base) !important;
    // font-family: Montserrat;
    font-size: 28px !important;
    font-style: bold !important;
    font-weight: 700 !important;
}

.v-data-table-header th {
    font-size: 16px !important;
}

.green-chip {
    color: white !important;
    background-color: green !important;
}

.amber-chip {
    background-color: #D97706 !important;
    color: #FFFFFF !important;
}

.red-chip {
    color: white !important;
    background-color: red !important;
}

.header-label {
    background-color: #f0f0f0;
    padding: 10px;
}

.class-cell {
    background-color: #f0f0f0;
    padding: 8px;
}

.projection-card {
    background: #FFFFFF !important;
}

.projection-title {
    font-size: 16px;
    font-weight: 700;
    color: #252538;
}

.projection-body {
    padding: 24px 40px !important;
}

/* Subtitulo de cada grafico na projecao (os tres com o mesmo estilo). */
.projection-chart-title {
    font-size: 15px;
    font-weight: 700;
    color: #374151;
    text-align: center;
    margin-bottom: 8px;
}

.projection-stats {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 40px;
    margin-bottom: 16px;

    > div {
        display: flex;
        flex-direction: column;
        align-items: center;
    }
}

.projection-stat-value {
    font-size: 44px;
    font-weight: 800;
    line-height: 1;
}

.projection-stat-label {
    font-size: 13px;
    font-weight: 700;
    color: #6B7280;
    letter-spacing: 0.04em;
    margin-top: 6px;
}

.voting-result-modal { border-radius:16px; max-height:90vh; display:flex; flex-direction:column; }
.voting-result-header { display:flex; justify-content:space-between; gap:16px; padding:24px 28px; border-bottom:1px solid #eeeaf3; flex-shrink:0; }
.voting-result-header h2 { font-size:24px; line-height:1.3; margin:4px 0; color:#252238; }
.voting-result-header p { margin:0; color:#756e83; overflow-wrap:anywhere; }
.voting-result-eyebrow { font-size:10px; font-weight:700; letter-spacing:1.2px; color:#756e83; }
.voting-result-body { padding:24px 28px !important; overflow-y:auto; min-height:0; }
.voting-result-verdict { display:flex; align-items:flex-start; gap:16px; border:1px solid; padding:22px; border-radius:12px; margin-bottom:20px; }
.verdict-success { background:#f0faf3; border-color:#cce9d6; }
.verdict-error { background:#fff5f5; border-color:#f3d8d8; }
.verdict-warning { background:#fffbeb; border-color:#f2dfa5; }
.verdict-icon .v-icon { font-size:32px; }
.voting-result-verdict h3 { font-size:28px; line-height:1.25; color:#252238; margin:4px 0 8px; }
.voting-result-verdict p { margin:0; color:#55515e; max-width:780px; line-height:1.6; }
.voting-result-metrics { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:14px; }
.voting-result-metric { border:1px solid #e9e5ef; border-radius:12px; padding:18px; }
.voting-result-metric h3 { font-size:13px; font-weight:600; color:#62596f; margin-bottom:16px; }
.voting-result-values { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
.voting-result-values span { display:block; font-size:12px; margin-bottom:4px; }
.voting-result-values strong { font-size:28px; line-height:1.2; }
.voting-result-modal .result-yes { color:#18713b; }
.voting-result-modal .result-no { color:#b52b35; }
.voting-result-note { display:flex; align-items:flex-start; gap:8px; font-size:12px; color:#746c80; line-height:1.6; margin:16px 0 24px; }
.voting-result-note .v-icon { margin-top:2px; }
.voting-result-classes h3 { font-size:16px; color:#252238; margin-bottom:12px; }
.voting-result-table-wrap { overflow-x:auto; border:1px solid #e9e5ef; border-radius:10px; }
.voting-result-table { border-collapse:collapse; width:100%; text-align:left; font-size:13px; }
.voting-result-table thead { background:#f7f5fa; color:#62596f; font-size:11px; }
.voting-result-table th,.voting-result-table td { padding:14px 12px; border-bottom:1px solid #eeeaf3; }
.voting-result-table td { white-space:nowrap; }
.voting-result-table tbody tr:last-child > * { border-bottom:0; }
.voting-result-footer { display:flex; justify-content:space-between; align-items:center; gap:16px; padding:16px 28px; border-top:1px solid #eeeaf3; background:#faf9fc; flex-shrink:0; }
.voting-result-footer > span { color:#746c80; font-size:12px; }
.voting-result-footer > div { display:flex; gap:8px; }
@media(max-width:600px) {
 .voting-result-header,.voting-result-body,.voting-result-footer { padding:16px !important; }
 .voting-result-metrics { grid-template-columns:1fr; }
 .voting-result-metric { padding:14px; }
 .voting-result-metric h3 { margin-bottom:10px; }
 .voting-result-values strong { font-size:24px; }
 .voting-result-verdict { padding:16px; gap:10px; }
 .voting-result-footer { flex-wrap:wrap; }
 .voting-result-footer > div { width:100%; flex-wrap:wrap; justify-content:flex-end; }
 .voting-result-footer .v-btn { letter-spacing:0; font-size:12px; }
}
.panorama-empty { min-height:260px; display:flex; align-items:center; justify-content:center; color:#746c80; font-size:13px; text-align:center; padding:24px; }
</style>
